import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectImagePreviewContext } from "features/map/layers/selectors"
import { selectPDFById } from "./PDFSlice"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import * as Icons from "@nova/component-icons"
import { setActiveImageId } from "features/map/layers/slice"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { ButtonIcon, ButtonPrimary } from "@nova/component-btn"
import { downloadUri } from "tools/browser/download"
import { useEffect, useState } from "react"
import cn from "classnames"
import { zIndex } from "@nova/constants"
import { throttle } from "nodash"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"

const PageNumberButton = ({
    number,
    page,
    setPage,
}: {
    number: number
    page: number
    setPage: (page: number) => void
}) => {
    const selected = number === page
    return (
        <button
            className={cn("w-5 h-5 rounded-md", { "bg-white-300": selected })}
            disabled={selected}
            onClick={() => setPage(number)}
        >
            <h5 className={cn({ "text-gray-0": selected, "text-gray-100": !selected })}>
                {number}
            </h5>
        </button>
    )
}

const clampXY = (x: number, y: number, maxX: number, maxY: number) => {
    const s = Math.min(maxX / x, maxY / y, 1)

    const nx = x * s
    const ny = y * s

    return [nx, ny]
}

export const PDFPreviewModalWithBoundary = () => {
    const previewModalContext = useAppSelector(selectImagePreviewContext())
    const pdf = useAppSelector(selectPDFById(previewModalContext?.id || ""))
    if (!previewModalContext || !pdf) return null
    return <PDFPreviewModal />
}

const defaultPdf = {}

const PDFPreviewModal = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const previewModalContext = useAppSelector(selectImagePreviewContext())! // must be defined for this component to render
    const pdf = useAppSelector(selectPDFById(previewModalContext.id)) || defaultPdf
    const dispatch = useAppDispatch()
    const { fullName } = useFullnameForUserId(pdf.createdBy?.userId)
    const download = throttle(() => {
        downloadUri(
            `${pdf.name}.pdf`,
            `/api/organization/${workspaceId}/job/${pdf.id}/map-direct.pdf`
        )
    }, 2000)

    const [page, setPage] = useState(1)
    const [imageDimensions, setImageDimensions] = useState({ width: 500, height: 386 })
    const [loadedSrc, setLoadedSrc] = useState<string>()

    const src = `/api/organization/${workspaceId}/job/${pdf.id}/preview.png?page=${page - 1}`
    const loading = loadedSrc !== src

    useEffect(() => {
        setPage(1)
    }, [pdf.id])

    const [width, height] = clampXY(imageDimensions.width, imageDimensions.height, 500, 500)
    return (
        <div
            className="flex flex-col gap-2"
            style={{
                zIndex: zIndex.SidePanel + 2,
                width: width + 16, // padding * 4 * 2
            }}
        >
            <div className="flex items-baseline gap-2">
                <h4 className="text-white overflow-hidden whitespace-nowrap text-ellipsis">
                    {pdf.name}
                </h4>
                <small className="text-gray-300 whitespace-nowrap">
                    {dateFromNumber(pdf.createdAt).toLocaleString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    })}
                </small>
                <Icons.SmallIcon
                    Icon={Icons.XClose}
                    className="cursor-pointer text-gray-100 hover:text-gray-0 mt-auto ml-auto"
                    onClick={() => dispatch(setActiveImageId(undefined))}
                />
            </div>
            {pdf.pages ? (
                <>
                    <div className="relative" style={{ width, height }}>
                        {loading && (
                            <div className="absolute w-full h-full flex">
                                <div className="loader m-auto" />
                            </div>
                        )}
                        <img
                            onLoad={({ target }) => {
                                setLoadedSrc(src)
                                setImageDimensions({
                                    width: (target as HTMLImageElement).naturalWidth,
                                    height: (target as HTMLImageElement).naturalHeight,
                                })
                            }}
                            src={src}
                            alt="pdf"
                            width={width}
                            height={height}
                        />
                    </div>
                    <div className="relative w-full flex items-center overflow-hidden">
                        <div className="flex gap-2 items-center flex-1 overflow-hidden">
                            <div className="w-5 min-w-[20px] aspect-square rounded-[5px] overflow-hidden">
                                <ProfilePhoto
                                    userId={pdf?.createdBy?.userId || ""}
                                    fullName={fullName || ""}
                                />
                            </div>
                            <h5 className="text-white overflow-hidden whitespace-nowrap text-ellipsis">
                                {fullName || "Unknown"}
                            </h5>
                        </div>
                        {pdf.pages > 1 && (
                            <div className="flex gap-2 items-center">
                                <ButtonIcon
                                    Icon={Icons.ChevronLeft}
                                    width={24}
                                    height={24}
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className="text-gray-100"
                                />
                                <PageNumberButton
                                    number={page > 2 ? Math.min(page - 1, pdf.pages - 2) : 1}
                                    page={page}
                                    setPage={setPage}
                                />
                                {pdf.pages > 1 && (
                                    <PageNumberButton
                                        number={
                                            page <= 2 ? 2 : page >= pdf.pages ? pdf.pages - 1 : page
                                        }
                                        page={page}
                                        setPage={setPage}
                                    />
                                )}
                                {pdf.pages > 2 && (
                                    <PageNumberButton
                                        number={
                                            page < pdf.pages - 1 ? Math.max(3, page + 1) : pdf.pages
                                        }
                                        page={page}
                                        setPage={setPage}
                                    />
                                )}
                                <ButtonIcon
                                    Icon={Icons.ChevronRight}
                                    width={24}
                                    height={24}
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === pdf.pages}
                                    className="text-gray-100"
                                />
                            </div>
                        )}
                        <div className="flex flex-1 justify-end relative">
                            <ButtonPrimary
                                title="Download"
                                LeftIcon={Icons.Download}
                                onClick={download}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div>Preview not available for this PDF.</div>
            )}
        </div>
    )
}
